import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import Layout from '../components/layout/Layout';
import PageHeader, { KpiTile, SectionCard, Spinner } from '../components/common/PageHeader';
import { NativeSelect } from '../components/common/FormField';
import { useAuth } from '../contexts/AuthContext';
import { queryKeys } from '../lib/queryClient';
import { api } from '../services/api';
import { CapitalSummary, RecurringExpense } from '../types';
import { financialYear, fmtCompact, fmtCurrency, fmtDate, fyBounds, recentFinancialYears } from '../utils/format';

// Two-series palette (validated for colour-blind separation and contrast):
// revenue = brand blue, expenses = brand orange. Text never takes these colours.
const REVENUE = '#0056d6';
const EXPENSES = '#e06a00';

interface DashboardData {
  revenue: number;
  gstBilled: number;
  invoicedTotal: number;
  invoiceCount: number;
  collected: number;
  expenses: number;
  netProfit: number;
  outstanding: number;
  outstandingCount: number;
  overdueAmount: number;
  overdueCount: number;
  capital: CapitalSummary;
  cashPosition: number;
  mrr: number;
  expenseByCategory: { categoryId: number; name: string; color: string; icon: string; total: number; count: number }[];
  trend: { month: string; label: string; revenue: number; collected: number; expenses: number; profit: number }[];
  openInvoices: { id: number; invoiceNumber: string; clientName: string; dueDate: string; totalAmount: number; isOverdue: boolean }[];
  dueRecurring: RecurringExpense[];
}

function TrendTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload as DashboardData['trend'][number];
  return (
    <div className="rounded-lg border border-ink-200 bg-white shadow-panel px-3 py-2 text-xs">
      <p className="font-bold text-ink-900 mb-1">{label}</p>
      <p className="flex items-center gap-2 text-ink-700"><span className="w-2 h-2 rounded-sm" style={{ background: REVENUE }} />Revenue <span className="ml-auto font-mono font-semibold">{fmtCurrency(row.revenue, 0)}</span></p>
      <p className="flex items-center gap-2 text-ink-700"><span className="w-2 h-2 rounded-sm" style={{ background: EXPENSES }} />Expenses <span className="ml-auto font-mono font-semibold">{fmtCurrency(row.expenses, 0)}</span></p>
      <p className="mt-1 pt-1 border-t border-ink-100 flex gap-4 text-ink-600">
        {row.profit >= 0 ? 'Profit' : 'Loss'} <span className="ml-auto font-mono font-semibold text-ink-900">{fmtCurrency(Math.abs(row.profit), 0)}</span>
      </p>
    </div>
  );
}

export default function Dashboard() {
  const { user, hasPermission } = useAuth();
  const [fy, setFy] = useState(financialYear());
  const range = fyBounds(fy);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.dashboard(range.from, range.to),
    queryFn: () => api.get<DashboardData>('/dashboard', range),
  });

  const maxCategory = Math.max(1, ...(data?.expenseByCategory.map((c) => c.total) ?? []));

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <PageHeader
          title={`Welcome, ${user?.name?.split(' ')[0] ?? ''}`}
          subtitle="Where the company's money came from and where it went"
          actions={
            <NativeSelect className="w-full sm:w-44" value={fy} onChange={setFy}
              options={recentFinancialYears().map((y) => ({ value: y, label: `FY ${y}` }))} />
          }
        />

        {isLoading || !data ? (
          <Spinner />
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
              <KpiTile label="Revenue (excl. GST)" value={fmtCompact(data.revenue)} icon="fa-arrow-trend-up" tone="brand"
                hint={`${data.invoiceCount} invoices · GST ${fmtCompact(data.gstBilled)}`} />
              <KpiTile label="Expenses" value={fmtCompact(data.expenses)} icon="fa-wallet" tone="accent" hint="Everything paid out" />
              <KpiTile label={data.netProfit >= 0 ? 'Net Profit' : 'Net Loss'} value={fmtCompact(Math.abs(data.netProfit))}
                icon={data.netProfit >= 0 ? 'fa-face-smile' : 'fa-triangle-exclamation'} tone={data.netProfit >= 0 ? 'green' : 'red'}
                hint="Revenue − expenses" />
              <KpiTile label="Cash Position (est.)" value={fmtCompact(data.cashPosition)} icon="fa-building-columns" tone="violet"
                hint="Capital + collections − expenses, all time" />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4 lg:mb-6">
              <KpiTile label="Collected" value={fmtCompact(data.collected)} icon="fa-circle-check" tone="green" hint="Payments received" />
              <KpiTile label="Outstanding" value={fmtCompact(data.outstanding)} icon="fa-hourglass-half" tone="ink"
                hint={data.overdueCount ? `${data.overdueCount} overdue · ${fmtCompact(data.overdueAmount)}` : `${data.outstandingCount} awaiting payment`} />
              <KpiTile label="Monthly Recurring Revenue" value={fmtCompact(data.mrr)} icon="fa-arrows-rotate" tone="brand" hint="Active subscriptions" />
              <KpiTile label="You Have Invested" value={fmtCompact(data.capital.ownerInvested)} icon="fa-sack-dollar" tone="accent" hint="Net of withdrawals" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4 lg:mb-6">
              <SectionCard title="Revenue vs Expenses — last 12 months" className="lg:col-span-2">
                <div className="h-72" role="img" aria-label="Bar chart of monthly revenue and expenses for the last 12 months">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.trend} barGap={2} barCategoryGap="24%" margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                      <CartesianGrid vertical={false} stroke="#e8eef8" />
                      <XAxis dataKey="label" tickLine={false} axisLine={{ stroke: '#d3dceb' }} tick={{ fontSize: 11, fill: '#5a6578' }} />
                      <YAxis tickLine={false} axisLine={false} width={56} tick={{ fontSize: 11, fill: '#5a6578' }}
                        tickFormatter={(v: number) => fmtCompact(v)} />
                      <Tooltip content={<TrendTooltip />} cursor={{ fill: 'rgba(10,18,36,0.04)' }} />
                      <Legend iconType="square" iconSize={9} wrapperStyle={{ fontSize: 12, color: '#343e51' }}
                        formatter={(value) => <span style={{ color: '#343e51' }}>{value}</span>} />
                      <Bar dataKey="revenue" name="Revenue" fill={REVENUE} radius={[4, 4, 0, 0]} maxBarSize={18} />
                      <Bar dataKey="expenses" name="Expenses" fill={EXPENSES} radius={[4, 4, 0, 0]} maxBarSize={18} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </SectionCard>

              <SectionCard title="Where the money went" actions={hasPermission('expenses:view') && <Link to="/expenses" className="text-xs font-semibold text-brand-600 hover:underline">View all</Link>}>
                {data.expenseByCategory.length === 0 ? (
                  <p className="text-sm text-ink-400 py-8 text-center">No expenses recorded in FY {fy}.</p>
                ) : (
                  <ul className="space-y-3">
                    {data.expenseByCategory.slice(0, 8).map((c) => (
                      <li key={c.categoryId} title={`${c.name}: ${fmtCurrency(c.total)} across ${c.count} entries`}>
                        <div className="flex items-center justify-between text-sm mb-1 gap-2">
                          <span className="text-ink-800 truncate"><i className={`fas ${c.icon} text-ink-400 w-5 text-xs`} />{c.name}</span>
                          <span className="font-mono font-semibold text-ink-900 shrink-0">{fmtCurrency(c.total, 0)}</span>
                        </div>
                        {/* One hue: bar length carries magnitude, the label carries identity */}
                        <div className="h-1.5 rounded-full bg-ink-100 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${Math.max(2, (c.total / maxCategory) * 100)}%`, background: EXPENSES }} />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SectionCard title="Awaiting payment" bodyClassName="p-0"
                actions={hasPermission('billing:view') && <Link to="/billing" className="text-xs font-semibold text-brand-600 hover:underline">Billing</Link>}>
                {data.openInvoices.length === 0 ? (
                  <p className="text-sm text-ink-400 py-8 text-center">No unpaid invoices. </p>
                ) : (
                  <ul className="divide-y divide-ink-100">
                    {data.openInvoices.map((i) => (
                      <li key={i.id}>
                        <Link to={`/billing/invoices/${i.id}`} className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-ink-50">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-ink-900 truncate">{i.clientName}</p>
                            <p className="text-xs text-ink-500 font-mono">{i.invoiceNumber}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-sm font-semibold text-ink-900">{fmtCurrency(i.totalAmount, 0)}</p>
                            <p className={`text-xs ${i.isOverdue ? 'text-red-600 font-semibold' : 'text-ink-500'}`}>
                              {i.isOverdue && <i className="fas fa-triangle-exclamation mr-1" />}
                              {i.isOverdue ? 'Overdue since' : 'Due'} {fmtDate(i.dueDate)}
                            </p>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>

              <SectionCard title="Bills due in the next 7 days" bodyClassName="p-0"
                actions={hasPermission('expenses:view') && <Link to="/recurring-expenses" className="text-xs font-semibold text-brand-600 hover:underline">Recurring bills</Link>}>
                {data.dueRecurring.length === 0 ? (
                  <p className="text-sm text-ink-400 py-8 text-center">Nothing due this week.</p>
                ) : (
                  <ul className="divide-y divide-ink-100">
                    {data.dueRecurring.map((r) => (
                      <li key={r.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-ink-900 truncate">{r.name}</p>
                          <p className="text-xs text-ink-500">{r.payee} · {r.category?.name}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-semibold text-ink-900">{fmtCurrency(r.totalAmount, 0)}</p>
                          <p className={`text-xs ${r.isDue ? 'text-red-600 font-semibold' : 'text-ink-500'}`}>{r.isDue ? 'Due' : 'On'} {fmtDate(r.nextDueDate)}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
